/**
 * Variant option availability narrowing by Product Status.
 *
 * Toggles the native theme class `is-unavailable` on each non-status option
 * value when no available variant exists for (this value x current Product
 * Status x other-dim selections). The theme's native CSS then renders the
 * standard sold-out styling (gray-out + native swatch X overlay for swatch
 * styles icon_circle / icon_square).
 *
 * Why this is needed: Shopify's built-in `value.available` (which sets
 * `is-unavailable` server-side) only narrows from earlier option positions
 * to later ones — so for option order [Color, Product Status], Status narrows
 * by Color but Color does NOT narrow by Status. This script adds the reverse
 * narrowing so Colors visually grey out / cross when their current Status
 * combination has no available variant.
 *
 * Example (Maison Y, Color x Product Status):
 *   - Status = Ready Stock => 001 Black + 007 Red gain `is-unavailable`.
 *   - Status = Pre-Order  => 002 Cream + 007 Red gain `is-unavailable`.
 *
 * The Product Status selector itself is never touched (native already narrows
 * Status correctly via server-side `value.available`).
 *
 * Activation markers (set by snippets/variant-picker.liquid):
 *   - data-product-status-option on the .option-selector for "Product Status".
 *     All other .option-selector elements inside <variant-picker> are treated
 *     as status-filterable (no extra marker needed).
 * Data source: <script type="application/json" data-all-variants> inside
 *   <variant-picker> with [{id, available, options:[...]}].
 *
 * Re-runs on every `on:variant:change` because variant-picker.js swaps the
 * fieldset DOM during variant changes, resetting native is-unavailable to
 * server-rendered state.
 */
(function () {
  var IS_UNAVAILABLE = 'is-unavailable';

  function normalize(s) {
    return String(s == null ? '' : s).toLowerCase().trim();
  }

  function cssEscape(value) {
    if (window.CSS && typeof CSS.escape === 'function') return CSS.escape(value);
    return String(value).replace(/(["\\\]\[\.\#:\(\)])/g, '\\$1');
  }

  function readVariants(picker) {
    var el = picker.querySelector('script[type="application/json"][data-all-variants]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function getSelectorValue(selector) {
    if (selector.dataset.selectorType === 'dropdown') {
      var opt = selector.querySelector('.custom-select__option[aria-selected="true"]');
      return opt && opt.dataset.value !== '' ? opt.dataset.value : null;
    }
    var input = selector.querySelector('input:checked');
    return input ? input.value : null;
  }

  // Variant matches when all dims except targetIndex match selection/status.
  // statusIndex uses currentStatusValue (null => wildcard).
  // Other non-target dims use selectedValues[i] (null => wildcard).
  function matchesOtherDims(variant, selectedValues, targetIndex, statusIndex, currentStatusValue) {
    for (var i = 0; i < variant.options.length; i += 1) {
      if (i === targetIndex) continue;
      if (i === statusIndex) {
        if (currentStatusValue == null) continue;
        if (normalize(variant.options[i]) !== normalize(currentStatusValue)) return false;
        continue;
      }
      if (selectedValues[i] == null) continue;
      if (variant.options[i] !== selectedValues[i]) return false;
    }
    return true;
  }

  function isValueAvailable(variants, value, selectedValues, targetIndex, statusIndex, currentStatusValue) {
    for (var i = 0; i < variants.length; i += 1) {
      var v = variants[i];
      if (!v.available) continue;
      if (v.options[targetIndex] !== value) continue;
      if (!matchesOtherDims(v, selectedValues, targetIndex, statusIndex, currentStatusValue)) continue;
      return true;
    }
    return false;
  }

  function applyUnavailable(selector, valueId, isAvailable) {
    var sel = '[data-value-id="' + cssEscape(valueId) + '"]';
    if (selector.dataset.selectorType === 'dropdown') {
      var opt = selector.querySelector('.custom-select__option' + sel);
      if (!opt) return;
      opt.classList.toggle(IS_UNAVAILABLE, !isAvailable);
      return;
    }
    var input = selector.querySelector('input' + sel);
    if (!input) return;
    input.classList.toggle(IS_UNAVAILABLE, !isAvailable);
  }

  function evaluatePicker(picker) {
    var variants = readVariants(picker);
    if (!variants || !variants.length) return;

    var selectors = Array.prototype.slice.call(
      picker.querySelectorAll('.option-selector:not(.option-selector--custom)')
    );
    if (!selectors.length) return;

    var statusOption = picker.querySelector('[data-product-status-option]');
    var statusIndex = statusOption ? selectors.indexOf(statusOption) : -1;
    if (statusIndex < 0) return; // nothing to narrow without a status selector

    var selectedValues = selectors.map(getSelectorValue);
    var currentStatusValue = selectedValues[statusIndex];

    for (var s = 0; s < selectors.length; s += 1) {
      if (s === statusIndex) continue;

      var selector = selectors[s];
      var nodes;
      if (selector.dataset.selectorType === 'dropdown') {
        nodes = selector.querySelectorAll('.custom-select__option[data-value-id]');
      } else {
        nodes = selector.querySelectorAll('input[data-value-id]');
      }

      for (var i = 0; i < nodes.length; i += 1) {
        var node = nodes[i];
        var val = selector.dataset.selectorType === 'dropdown' ? node.dataset.value : node.value;
        var valueId = node.getAttribute('data-value-id');
        var available = isValueAvailable(variants, val, selectedValues, s, statusIndex, currentStatusValue);
        applyUnavailable(selector, valueId, available);
      }
    }
  }

  function evaluateAll(root) {
    var pickers = (root || document).querySelectorAll('variant-picker');
    for (var i = 0; i < pickers.length; i += 1) {
      evaluatePicker(pickers[i]);
    }
  }

  document.addEventListener('on:variant:change', function (evt) {
    var picker = evt.target && evt.target.closest ? evt.target.closest('variant-picker') : null;
    if (picker) {
      evaluatePicker(picker);
    } else {
      evaluateAll(document);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { evaluateAll(document); });
  } else {
    evaluateAll(document);
  }
})();
